import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { logger } from '../lib/logger';
import { AuthError } from '../lib/errors';
import { getApiUrl } from '../lib/env';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string, accountType: 'individual' | 'company', captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  getUserAccountType: () => 'individual' | 'company' | null;
  /** Whether the current user is a super admin (can view all dashboards) */
  isSuperAdmin: boolean;
  /** For super admins: the currently active view mode */
  superAdminViewMode: 'individual' | 'company';
  /** For super admins: toggle between individual/company view */
  setSuperAdminViewMode: (mode: 'individual' | 'company') => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [superAdminViewMode, setSuperAdminViewMode] = useState<'individual' | 'company'>('individual');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // ⚡ Super Admin toggle: Ctrl+Shift+A
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsSuperAdmin(prev => {
          const next = !prev;
          console.log(next ? '⚡ SUPER ADMIN MODE: ON' : '⚡ SUPER ADMIN MODE: OFF');
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const API_URL = getApiUrl();

  // ============================================================================
  // 🚧 DEV MODE AUTHENTICATION BYPASS
  // ============================================================================
  // This bypass is for DEVELOPMENT ONLY to speed up testing.
  // It auto-authenticates a test user without requiring Firebase/backend auth.
  //
  // HOW TO USE:
  // - Set localStorage.setItem('DEV_MODE', 'true') in browser console to enable
  // - Set localStorage.setItem('DEV_MODE_ACCOUNT_TYPE', 'company') for company user
  // - Set localStorage.setItem('DEV_MODE_ACCOUNT_TYPE', 'individual') for individual user
  // - Set localStorage.setItem('DEV_MODE_ACCOUNT_TYPE', 'superAdmin') for SUPER ADMIN (both views)
  // - Set localStorage.setItem('DEV_MODE_USER_NAME', 'Your Name') to customize name
  //
  // HOW TO DISABLE:
  // - Set localStorage.removeItem('DEV_MODE') or set to 'false'
  //
  // ⚠️  BEFORE PRODUCTION: Remove or disable this bypass!
  // ============================================================================
  const DEV_MODE_ENABLED = localStorage.getItem('DEV_MODE') === 'true';

  const createDevModeUser = (): User => {
    const accountType = localStorage.getItem('DEV_MODE_ACCOUNT_TYPE') || 'individual';
    const isSuperAdminMode = accountType === 'superAdmin';
    const userName = localStorage.getItem('DEV_MODE_USER_NAME') || (isSuperAdminMode ? '⚡ Super Admin' : accountType === 'company' ? 'TestCompany Corp' : 'Test User');
    const userEmail = isSuperAdminMode ? 'admin@humanreplica.ai' : accountType === 'company' ? 'company@humanreplica.ai' : 'test@humanreplica.ai';

    console.log('🚧 DEV MODE: Auto-authenticating as', { userName, accountType, email: userEmail, isSuperAdmin: isSuperAdminMode });

    return {
      uid: 'dev-mode-test-user-id',
      email: userEmail,
      displayName: userName,
      emailVerified: true,
      getIdToken: async () => 'dev-mode-token',
      metadata: { creationTime: new Date().toISOString(), lastSignInTime: new Date().toISOString() },
      providerData: [],
      refreshToken: '',
      tenantId: null,
      delete: async () => { },
      reload: async () => { },
      toJSON: () => ({}),
      isAnonymous: false,
      phoneNumber: null,
      photoURL: null,
      providerId: 'dev-mode',
    } as unknown as User;
  };

  /**
   * Sync Firebase auth with backend: exchange Firebase ID token for backend JWT.
   * This ensures backend API calls work after any Firebase auth event.
   */
  const syncBackendSession = async (firebaseUser: User): Promise<void> => {
    // Skip if we already have a valid backend token
    const existingToken = localStorage.getItem('accessToken');
    if (existingToken) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const checkRes = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${existingToken}` },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (checkRes.ok) return; // Backend token still valid
      } catch (_) { /* token invalid or backend unreachable, continue */ }
    }

    // Try to login on the backend using email — the user should already exist
    try {
      const firebaseToken = await firebaseUser.getIdToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const meRes = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (meRes.ok) {
        localStorage.setItem('accessToken', firebaseToken);
        return;
      }
    } catch (e) {
      logger.error('Backend session sync failed (timeout or unreachable)', e);
    }
  };

  const checkLocalSession = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        const mockUser = {
          // Prefer firebaseUid so Firestore queries (where creatorId == uid) keep working.
          // Fall back to Prisma DB id for backend-only (non-Firebase) accounts.
          uid: data.user.firebaseUid || data.user.id,
          email: data.user.email,
          displayName: data.user.name,
          emailVerified: true,
          getIdToken: async () => token,
          metadata: { creationTime: data.user.createdAt, lastSignInTime: new Date().toISOString() },
          providerData: [],
          refreshToken: '',
          tenantId: null,
          delete: async () => { },
          reload: async () => { },
          toJSON: () => ({}),
          isAnonymous: false,
          phoneNumber: null,
          photoURL: null,
          providerId: 'custom',
        } as unknown as User;
        setUser(mockUser);
      } else {
        localStorage.removeItem('accessToken');
      }
    } catch (e) {
      console.error('Local session check failed (backend may be offline)', e);
      // Don't clear token on timeout — backend might just be temporarily down
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 🚧 DEV MODE: Skip all auth if enabled
    if (DEV_MODE_ENABLED) {
      console.log('🚧 DEV MODE ACTIVE: Skipping Firebase auth, using test user');
      setUser(createDevModeUser());
      setIsLoading(false);
      return;
    }

    // Listen for auth state changes (Firebase)
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.debug('Auth state changed', { userId: firebaseUser?.uid, email: firebaseUser?.email });
      if (firebaseUser) {
        setUser(firebaseUser);
        // Sync with backend (non-blocking — don't let backend failure block UI)
        try {
          await syncBackendSession(firebaseUser);
        } catch (e) {
          logger.error('Backend sync failed, continuing with Firebase-only auth', e);
        }
        setIsLoading(false);
      } else {
        // No Firebase user — check for backend-only local JWT session
        checkLocalSession();
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string, captchaToken?: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    // Try Local API first
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, captchaToken }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('accessToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        // Re-run session check or set user directly
        await checkLocalSession();
        return { success: true };
      } else if (res.status !== 404) {
        // If 401, definitely invalid creds for local.
        if (res.status === 401) {
          const errData = await res.json();
          setIsLoading(false);
          return { success: false, error: errData.error || 'Invalid credentials' };
        }
      }
    } catch (e) {
      console.warn('Local login failed, falling back to Firebase', e);
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      logger.debug('Login successful (Firebase)', { userId: userCredential.user.uid });
      // Sync backend session so API calls work after Firebase-only login
      await syncBackendSession(userCredential.user);
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      const authError = AuthError.fromFirebaseError(error as { code?: string; message?: string });
      logger.error('Login error:', authError.message);
      setIsLoading(false);
      return { success: false, error: authError.message };
    }
  };

  const signup = async (name: string, email: string, password: string, accountType: 'individual' | 'company', captchaToken?: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    try {
      // Step 1: Create user in Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Step 2: Update Firebase profile
      await updateProfile(userCredential.user, {
        displayName: name,
      });

      // Step 3: Get Firebase ID token
      const firebaseToken = await userCredential.user.getIdToken();

      // Step 4: Register user in backend database
      // This creates the user record that the backend needs for authentication
      try {
        const res = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`, // Send Firebase token
          },
          body: JSON.stringify({
            email,
            password, // Backend will hash this
            name,
            accountType,
            captchaToken,
          }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          // Surface backend validation errors (e.g. password too weak, duplicate email)
          const errorMsg = errData.details
            ? Object.values(errData.details).flat().join('. ')
            : errData.error || 'Registration failed. Please try again.';
          logger.error('Backend registration failed', errData);
          // Delete the Firebase user since backend registration failed
          try { await userCredential.user.delete(); } catch (_) { /* best-effort cleanup */ }
          setIsLoading(false);
          return { success: false, error: errorMsg };
        }
        // Step 5: Store backend tokens for persistent session
        const regData = await res.json();
        if (regData.accessToken) {
          localStorage.setItem('accessToken', regData.accessToken);
        }
        if (regData.refreshToken) {
          localStorage.setItem('refreshToken', regData.refreshToken);
        }
      } catch (backendError) {
        logger.error('Backend registration error:', backendError);
        // Delete the Firebase user since backend registration failed
        try { await userCredential.user.delete(); } catch (_) { /* best-effort cleanup */ }
        setIsLoading(false);
        return { success: false, error: 'Could not connect to the server. Please try again.' };
      }

      // Store account type in localStorage as a temporary solution
      localStorage.setItem(`user_${userCredential.user.uid}_account_type`, accountType);

      logger.debug('Signup successful', { userId: userCredential.user.uid });
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      const authError = AuthError.fromFirebaseError(error as { code?: string; message?: string });
      logger.error('Signup error:', authError.message);
      setIsLoading(false);
      return { success: false, error: authError.message };
    }
  };

  const signInWithGoogle = async (): Promise<{ success: boolean; error?: string }> => {
    logger.debug('Starting Google sign in');
    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      logger.debug('Google sign in successful', { userId: result.user.uid });
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      const authError = AuthError.fromFirebaseError(error as { code?: string; message?: string });
      logger.error('Google sign in error:', authError.message);
      setIsLoading(false);
      return { success: false, error: authError.message };
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await signOut(auth);
      // Phase 4: Call backend logout to blacklist the token
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          await fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
        } catch (_) { /* best-effort server-side logout */ }
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      logger.debug('Signed out successfully');
      setUser(null);
    } catch (error) {
      logger.error('Logout error:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Super admin is now a runtime toggle via Ctrl+Shift+A (no localStorage needed)

  const getUserAccountType = (): 'individual' | 'company' | null => {
    if (!user) return null;

    // Super Admin mode: return the currently selected view mode
    if (isSuperAdmin) {
      return superAdminViewMode;
    }

    // Check for company test account
    if (user.email === 'company@humanreplica.ai') return 'company';

    // Check if displayName contains company indicators
    if (user.displayName?.toLowerCase().includes('corp') ||
      user.displayName?.toLowerCase().includes('company') ||
      user.displayName?.toLowerCase().includes('enterprise')) {
      return 'company';
    }

    // Check if it's a local mocked user (admin)
    if ((user as any).providerId === 'custom') return 'individual';

    // TODO: Replace localStorage with Firestore for production
    // Get account type from localStorage (temporary solution)
    const accountType = localStorage.getItem(`user_${user.uid}_account_type`);
    return (accountType as 'individual' | 'company') || 'individual';
  };

  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      logger.debug('Password reset email sent', { email });
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      const authError = AuthError.fromFirebaseError(error as { code?: string; message?: string });
      logger.error('Password reset error:', authError.message);
      setIsLoading(false);
      return { success: false, error: authError.message };
    }
  };

  const value = {
    user,
    login,
    signup,
    signInWithGoogle,
    logout,
    resetPassword,
    isLoading,
    getUserAccountType,
    isSuperAdmin,
    superAdminViewMode,
    setSuperAdminViewMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};